import React from 'react'
import { Box, IconButton } from '@mui/material'
import { Activity } from '../../types'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../../../constants/colors'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { getDisplayDurationMinutes } from '../../utils/activityConfiguration'
import { TESTIDS } from '../../../../../config/testids'

export interface ActivityCardProps {
  activity: Activity
  isSelected?: boolean
  onClick?: () => void
  onAdd?: () => void
  showTooltip?: boolean
  onTooltipShow?: (activityId: string) => void
  onTooltipHide?: () => void
  className?: string
  variant?: 'selectable' | 'display'
  disabled?: boolean
  currentCount?: number
  limit?: number | null
}

export const ActivityCard: React.FC<ActivityCardProps> = ({
  activity,
  isSelected = false,
  onClick,
  onAdd,
  showTooltip = false,
  onTooltipShow,
  onTooltipHide,
  className = '',
  variant = 'selectable',
  disabled = false,
  currentCount = 0,
  limit = null
}) => {
  const Icon = activity.icon
  const isClickable = variant === 'selectable' && (onClick || onAdd) && !disabled
  const limitText = limit == null ? '∞' : `${currentCount}/${limit}`

  const displayDuration = getDisplayDurationMinutes(activity)

  const handleClick = () => {
    if (isClickable) {
      if (onAdd) {
        onAdd()
      } else if (onClick) {
        onClick()
      }
    }
  }

  const handleTooltipShow = () => {
    if (onTooltipShow) {
      onTooltipShow(activity.id)
    }
  }

  const handleTooltipHide = () => {
    if (onTooltipHide) {
      onTooltipHide()
    }
  }

  return (
    <Box
      data-testid={`${TESTIDS.SPRINT_ACTIVITY_CARD}-${activity.id}`}
      className={`${isClickable ? 'cursor-pointer' : disabled ? 'cursor-not-allowed opacity-50' : ''} ${
        isSelected ? 'ring-2 ring-orange-500' : ''
      } ${className}`}
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 3,
        px: 3,
        py: 2.5,
        transition: 'all 0.2s ease',
        borderRadius: '12px',
        backgroundColor: isSelected ? INTERAX_COLORS.heritageBlue : INTERAX_COLORS.heritageBlue40,
        borderColor: isSelected ? INTERAX_COLORS.sun : INTERAX_COLORS.thunderGrey,
        borderWidth: '1px',
        borderStyle: 'solid',
        width: '210px',
        height: '86px',
        pointerEvents: disabled ? 'none' : 'auto'
      }}
      onClick={handleClick}
    >
      {/* Info Icon - only show for selectable variant */}
      {variant === 'selectable' && (
        <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
          <IconButton
            onMouseEnter={handleTooltipShow}
            onMouseLeave={handleTooltipHide}
            size="small"
            sx={{
              p: 0.5,
              color: INTERAX_COLORS.sun
            }}
          >
            <InformationCircleIcon className="w-4 h-4" />
          </IconButton>

          {/* Tooltip */}
          {showTooltip && (
            <Box
              sx={{
                position: 'absolute',
                zIndex: 50,
                right: 0,
                top: 28,
                width: '192px',
                p: 1.5,
                borderRadius: '8px',
                backgroundColor: INTERAX_COLORS.offBlack,
                color: INTERAX_COLORS.white,
                fontSize: '12px',
                lineHeight: '16px'
              }}
            >
              {activity.description}
            </Box>
          )}
        </Box>
      )}

      {/* Icon on the left */}
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          backgroundColor: INTERAX_COLORS.sun,
          color: INTERAX_COLORS.offBlack
        }}
      >
        <Icon className="w-5 h-5" />
      </Box>

      {/* Content in the middle */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          component="h4"
          className="text-white"
          sx={{
            m: 0,
            mb: 0.5,
            fontFamily: INTERAX_TYPOGRAPHY.body3.fontFamily,
            fontWeight: INTERAX_TYPOGRAPHY.body3.fontWeight,
            fontSize: INTERAX_TYPOGRAPHY.body4.fontSize,
            lineHeight: INTERAX_TYPOGRAPHY.body3.lineHeight
          }}
        >
          {activity.title}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <svg
            className="w-4 h-4"
            style={{ color: INTERAX_COLORS.thunderGrey }}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
              clipRule="evenodd"
            />
          </svg>
          <Box
            component="p"
            sx={{
              m: 0,
              color: INTERAX_COLORS.thunderGrey,
              fontFamily: INTERAX_TYPOGRAPHY.body3.fontFamily,
              fontWeight: INTERAX_TYPOGRAPHY.body3.fontWeight,
              fontSize: INTERAX_TYPOGRAPHY.body3.fontSize,
              lineHeight: INTERAX_TYPOGRAPHY.body3.lineHeight
            }}
          >
            {displayDuration} min
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          position: 'absolute',
          bottom: 8,
          right: 8,
          px: 1.5,
          py: 0.5,
          borderRadius: '999px',
          backgroundColor: disabled ? INTERAX_COLORS.thunderGrey : INTERAX_COLORS.sun,
          color: disabled ? INTERAX_COLORS.white : INTERAX_COLORS.offBlack,
          fontFamily: INTERAX_TYPOGRAPHY.buttonSmall.fontFamily,
          fontWeight: INTERAX_TYPOGRAPHY.buttonSmall.fontWeight,
          fontSize: INTERAX_TYPOGRAPHY.body4,
          lineHeight: INTERAX_TYPOGRAPHY.buttonSmall.lineHeight
        }}
      >
        {limitText}
      </Box>
    </Box>
  )
}
